import { useCallback, useEffect, useRef, useState } from 'react';
import './Dropdown.css';

export function DropdownMenu({ items, dark, onClose }) {
  const [focusIdx, setFocusIdx] = useState(-1);
  const menuRef = useRef(null);

  const selectableItems = items.filter((i) => i.type === 'item');

  const handleKey = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((i) => Math.min(i + 1, selectableItems.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((i) => Math.max(i - 1, 0));
      }
      if (e.key === 'Enter' && focusIdx >= 0) {
        selectableItems[focusIdx]?.onSelect?.();
      }
    },
    [focusIdx, selectableItems, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  let selectableCount = 0;

  return (
    <div
      ref={menuRef}
      className={`dd-menu${dark ? ' dd-menu--dark' : ''}`}
      role="menu"
    >
      {items.map((item, idx) => {
        if (item.type === 'label') {
          return (
            <div key={idx} className="dd-menu__label">
              {item.text}
            </div>
          );
        }
        if (item.type === 'sep') {
          return <div key={idx} className="dd-menu__sep" role="separator" />;
        }
        // Non-interactive org header: avatar + org name + capability subtitle.
        if (item.type === 'header') {
          return (
            <div key={idx} className="dd-menu__header">
              {item.avatar != null && (
                <span
                  className="dd-menu__header-av"
                  style={
                    item.avatarColor
                      ? { background: item.avatarColor }
                      : undefined
                  }
                  aria-hidden="true"
                >
                  {item.avatar}
                </span>
              )}
              <span className="dd-menu__header-info">
                <span className="dd-menu__header-title">{item.title}</span>
                {item.subtitle && (
                  <span className="dd-menu__header-meta">{item.subtitle}</span>
                )}
              </span>
            </div>
          );
        }
        // Non-interactive identity block: person name + meta (role · email).
        if (item.type === 'userblock') {
          return (
            <div key={idx} className="dd-menu__userblock">
              <span className="dd-menu__userblock-name">{item.name}</span>
              {item.meta && (
                <span className="dd-menu__userblock-meta">{item.meta}</span>
              )}
            </div>
          );
        }
        const sIdx = selectableCount++;
        const isFocused = sIdx === focusIdx;
        return (
          <button
            key={idx}
            className={`dd-menu__item${isFocused ? ' dd-menu__item--focused' : ''}`}
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              item.onSelect?.();
              if (!item.keepOpen) onClose?.();
            }}
          >
            {item.ico && <span className="dd-menu__ico">{item.ico}</span>}
            <span>{item.text}</span>
            {item.kbd && <span className="dd-menu__kbd">{item.kbd}</span>}
            {item.chev && (
              <span className="dd-menu__chev" style={{ marginLeft: 'auto' }}>
                {item.chev}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function Dropdown({ trigger, items, dark }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={wrapRef} className="dd-wrap">
      <div onClick={() => setOpen((v) => !v)}>{trigger}</div>
      {open && (
        <DropdownMenu
          items={items}
          dark={dark}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
